import * as React from "react";
import { useState } from "react";
import { WrappedComponentProps, injectIntl, IntlShape } from "react-intl";
import { PapiClientProps, withPapi } from "../Api/PapiHoc";
import { TradeHistorySearchForm, SearchParameters } from "./TradeHistorySearchForm";
import { TradeHistoryGrid } from "./TradeHistoryGrid";
import { Spin, Icon, Button } from "antd";
import { Currency } from "swan-public-api-client";
import { convertToTradeHistoryGridRows, TradeHistoryGridRow } from "./TradeHistoryGridRow";
import { CSVLink } from "react-csv";
import { openingOrClosingToStringConverter, currencyToStringConverter } from "../Localisation/Converters";

interface CsvRow {
  Reference: string;
  Market: string;
  OpeningOrClosing: string;
  Quantity: number;
  Currency: string;
  BookingDate: string;
  BookingPrice: number;
  ProfitAndLoss?: number;
  BaseConsideration?: number;
  DeliveryConsideration?: number;
}

interface Props extends WrappedComponentProps, PapiClientProps {
  tradingAccountKey: string;
  baseCurrency: Currency;
  currencies: Map<number, Currency>;
}

function TradeHistoryCmp(props: Props) {
  const [from, setFrom] = useState(undefined as Date | undefined);
  const [to, setTo] = useState(undefined as Date | undefined);
  const [hasSearched, setHasSearched] = useState(false);
  const [searching, setSearching] = useState(false);
  const [history, setHistory] = useState([] as TradeHistoryGridRow[]);

  const onSearch = async (searchParameters: SearchParameters) => {
    try {
      setSearching(true);
      setHasSearched(true);

      const searchResults = await props.papiClient.getTradeHistory(
        props.tradingAccountKey,
        searchParameters.from,
        searchParameters.to
      );
      const value = convertToTradeHistoryGridRows(searchResults.Content, parseInt(props.baseCurrency.Key, 10));
      setHistory(value);
    } finally {
      setSearching(false);
    }
  };

  const enableDownloadButton = history.length > 0;
  const downloadButton =
    <Button
      type="primary"
      icon="download"
      disabled={!enableDownloadButton}
    >
      {props.intl.formatMessage({ id: "tradeHistory.downloadButton" })}
    </Button>;

  const tradeHistory = (
    <>
      <TradeHistorySearchForm
        onSearch={onSearch}
        from={from}
        onFromChange={setFrom}
        to={to}
        onToChange={setTo}
        extra={
          enableDownloadButton
            ?
              <CSVLink
                data={convertResultsToCsvData(
                  history,
                  props.currencies,
                  props.intl)}
                filename="axi_trade_history.csv"
              >
                {downloadButton}
              </CSVLink>
            : downloadButton
        }
      />
      <TradeHistoryGrid
        history={history}
        baseCurrency={props.baseCurrency}
        currencies={props.currencies}
        bordered={false}
        locale={{ emptyText: props.intl.formatMessage({ id: hasSearched ? "tradeHistory.emptySearchResultsEmptyText" : "tradeHistory.notYetSearchedEmptyText" })}}
      />
    </>
  );

  return (
    <div className="tradeHistory">
      {searching
        ? (
          <Spin
            indicator={(<Icon type="loading"/>)}
            tip={props.intl.formatMessage({ id: "tradeHistory.searching" })}
            delay={100}
          >
            {tradeHistory}
          </Spin>
        )
        : tradeHistory
      }
    </div>
  );
}

function convertResultsToCsvData(
  searchResults: TradeHistoryGridRow[],
  currencies: Map<number, Currency>,
  intl: IntlShape
): CsvRow[] {
  return searchResults.map(x => {
    return {
      Reference: x.Reference,
      Market: x.MarketName,
      OpeningOrClosing: openingOrClosingToStringConverter(x.OpeningOrClosing, intl),
      Quantity: x.OriginalQuantity,
      Currency: currencyToStringConverter(currencies.get(x.CurrencyId), intl),
      BookingDate: `${intl.formatDate(x.BookingTime)} ${intl.formatTime(x.BookingTime)}`,
      BookingPrice: x.BookingPrice,
      ProfitAndLoss: x.Pnl,
      BaseConsideration: x.Consideration?.BaseConsideration?.Value,
      DeliveryConsideration: x.Consideration?.DeliveryConsideration?.Value
    }
  });
}

const TradeHistoryWithIntl = injectIntl(TradeHistoryCmp);

export const TradeHistory = withPapi(TradeHistoryWithIntl);
